// =============================================================================
// permissions_catalog.js
//
// Two related catalogs drive the roles editor:
//   1. PERMISSION_MODULE_GROUPS: the backend-backed module keys that can be
//      persisted today.
//   2. PERMISSION_GRID_GROUPS: the display grid shown on the page. This may be
//      more detailed than the stored module model and can therefore contain
//      repeated module keys or read-only informational rows.
// =============================================================================

export const PERMISSION_MODULE_GROUPS = [
  {
    group: "Dashboard",
    items: [
      { moduleKey: "dashboard" },
    ],
  },
  {
    group: "Messaging",
    items: [
      { moduleKey: "messaging.messages" },
      { moduleKey: "messaging.scheduler" },
      { moduleKey: "messaging.alerts" },
      { moduleKey: "messaging.templates" },
      { moduleKey: "messaging.responses" },
      { moduleKey: "messaging.extend_stay" },
      { moduleKey: "messaging.surveys" },
      { moduleKey: "messaging.chatbot" },
      { moduleKey: "messaging.ai" },
    ],
  },
  {
    group: "Guests",
    items: [
      { moduleKey: "messaging.guests" },
      { moduleKey: "messaging.guests_message" },
    ],
  },
  {
    group: "Contacts",
    items: [
      { moduleKey: "messaging.contacts" },
      { moduleKey: "messaging.contacts_message" },
    ],
  },
  {
    group: "Smart Locks",
    items: [
      { moduleKey: "admin.integrations" },
    ],
  },
  {
    group: "CRM",
    items: [
      { moduleKey: "crm.leads" },
      { moduleKey: "crm.guest_leads" },
      { moduleKey: "crm.owner_leads" },
      { moduleKey: "crm.contacts" },
      { moduleKey: "crm.pipelines" },
      { moduleKey: "crm.notes" },
      { moduleKey: "crm.activities" },
      { moduleKey: "crm.custom_fields" },
      { moduleKey: "operations.tasks" },
    ],
  },
  {
    group: "Guides",
    items: [
      { moduleKey: "guides.master" },
      { moduleKey: "guides.home" },
      { moduleKey: "guides.area" },
      { moduleKey: "guides.extras" },
      { moduleKey: "guides.subscriptions" },
      { moduleKey: "guides.payments" },
    ],
  },
  {
    group: "Operations",
    items: [
      { moduleKey: "operations.calendar" },
      { moduleKey: "operations.work_orders" },
      { moduleKey: "operations.inspections" },
      { moduleKey: "operations.inventory" },
      { moduleKey: "operations.checklists" },
      { moduleKey: "operations.standards" },
      { moduleKey: "operations.work_schedules" },
    ],
  },
  {
    group: "Reports",
    items: [
      { moduleKey: "reports" },
    ],
  },
  {
    group: "Admin",
    items: [
      { moduleKey: "admin.users" },
      { moduleKey: "admin.roles" },
      { moduleKey: "admin.settings" },
      { moduleKey: "admin.billing" },
    ],
  },
];

const MODULE_LABELS = {
  dashboard: "Dashboard",
  "messaging.messages": "Messages",
  "messaging.scheduler": "Scheduled Messages",
  "messaging.alerts": "Alerts",
  "messaging.templates": "Message Templates",
  "messaging.responses": "Saved Responses",
  "messaging.extend_stay": "Extend stay",
  "messaging.surveys": "Surveys",
  "messaging.chatbot": "AI Chatbot",
  "messaging.guests": "Guests",
  "messaging.guests_message": "Guest Messaging",
  "messaging.contacts": "Contacts",
  "messaging.contacts_message": "Contact Messaging",
  "admin.integrations": "Smart Locks",
  "crm.leads": "Leads",
  "crm.guest_leads": "Guest Leads",
  "crm.owner_leads": "Owner Leads",
  "crm.contacts": "CRM Contacts",
  "crm.pipelines": "Pipeline Boards",
  "crm.notes": "Notes",
  "crm.activities": "Activities",
  "crm.custom_fields": "Custom Fields",
  "operations.tasks": "Tasks",
  "guides.master": "Guide Designer",
  "guides.home": "Home Guides",
  "guides.area": "Area Guides",
  "guides.extras": "Extras Guide",
  "guides.subscriptions": "Guestbook Subscriptions",
  "guides.payments": "Extras Orders",
  "operations.calendar": "Task calendar",
  "operations.work_orders": "Work orders",
  "operations.inspections": "Inspection Board",
  "operations.inventory": "Inventory",
  "operations.checklists": "Checklists",
  "operations.standards": "Property Setup & Standards",
  "messaging.ai": "AI Assistant",
  "operations.work_schedules": "Work Schedules",
  reports: "Reports",
  "admin.users": "Users & Permissions",
  "admin.roles": "Roles & Permissions",
  "admin.settings": "Settings",
  "admin.billing": "Billing",
};

export const PERMISSION_GRID_GROUPS = [
  {
    group: "Dashboard",
    items: [
      { id: "dashboard-view", moduleKey: "dashboard", moduleLabel: "Dashboard", permissionLabel: "Dashboard", accessLevels: ["none", "view"] },
    ],
  },
  {
    group: "Messaging",
    items: [
      { id: "messaging-messages", moduleKey: "messaging.messages", moduleLabel: "Messages", permissionLabel: "Messages", accessLevels: ["none", "edit"] },
      { id: "messaging-scheduler", moduleKey: "messaging.scheduler", moduleLabel: "Scheduled Messages", permissionLabel: "Scheduled messages" },
      { id: "messaging-alerts", moduleKey: "messaging.alerts", moduleLabel: "Alerts", permissionLabel: "Alerts" },
      { id: "messaging-templates", moduleKey: "messaging.templates", moduleLabel: "Message Templates", permissionLabel: "Message templates" },
      { id: "messaging-responses", moduleKey: "messaging.responses", moduleLabel: "Saved Responses", permissionLabel: "Saved responses" },
      { id: "messaging-surveys", moduleKey: "messaging.surveys", moduleLabel: "Surveys", permissionLabel: "Surveys" },
      { id: "messaging-extend-stay", moduleKey: "messaging.extend_stay", moduleLabel: "Extend stay", permissionLabel: "Extend stay", accessLevels: ["none", "edit"] },
      { id: "messaging-ai", moduleKey: "messaging.ai", moduleLabel: "AI Assistant", permissionLabel: "AI message composer", accessLevels: ["none", "edit"] },
      { id: "messaging-chatbot", moduleKey: "messaging.chatbot", moduleLabel: "AI Chatbot", permissionLabel: "AI chatbot management" },
    ],
  },
  {
    group: "Guests",
    items: [
      { id: "guests-access", moduleKey: "messaging.guests", moduleLabel: "Guests", permissionLabel: "Guests" },
      { id: "guests-message", moduleKey: "messaging.guests_message", moduleLabel: "Guests", permissionLabel: "Send message to guests", accessLevels: ["none", "edit"] },
    ],
  },
  {
    group: "Contacts",
    items: [
      { id: "contacts-access", moduleKey: "messaging.contacts", moduleLabel: "Contacts", permissionLabel: "Contacts" },
      { id: "contacts-message", moduleKey: "messaging.contacts_message", moduleLabel: "Contacts", permissionLabel: "Send message to contacts", accessLevels: ["none", "edit"] },
    ],
  },
  {
    group: "Smart Locks",
    items: [
      { id: "smart-locks-all", moduleKey: "admin.integrations", moduleLabel: "Smart Locks", permissionLabel: "Smart Locks", accessLevels: ["none", "edit"] },
    ],
  },
  {
    group: "CRM",
    items: [
      { id: "crm-contacts", moduleKey: "crm.contacts", moduleLabel: "CRM", permissionLabel: "CRM contacts/leads" },
      { id: "crm-pipelines", moduleKey: "crm.pipelines", moduleLabel: "CRM", permissionLabel: "Manage Pipeline Boards" },
      { id: "crm-notes", moduleKey: "crm.notes", moduleLabel: "CRM", permissionLabel: "Manage Notes" },
      { id: "crm-activities", moduleKey: "crm.activities", moduleLabel: "CRM", permissionLabel: "Manage Activities" },
      { id: "crm-custom-fields", moduleKey: "crm.custom_fields", moduleLabel: "CRM", permissionLabel: "Manage Custom fields" },
    ],
  },
  {
    group: "Guides",
    items: [
      { id: "guides-builder", moduleKey: "guides.master", moduleLabel: "Guide Builder", permissionLabel: "Edit and publish Guide Designer", accessLevels: ["none", "edit"] },
      { id: "guides-home", moduleKey: "guides.home", moduleLabel: "Home Guides", permissionLabel: "Manage property guide content" },
      { id: "guides-area", moduleKey: "guides.area", moduleLabel: "Area Guides", permissionLabel: "Manage area guide content" },
      { id: "guides-extras", moduleKey: "guides.extras", moduleLabel: "Extras Guide", permissionLabel: "Manage extras and add-ons content" },
      { id: "guides-subscriptions", moduleKey: "guides.subscriptions", moduleLabel: "Guestbook Subscriptions", permissionLabel: "Manage guestbook subscriptions" },
      { id: "guides-payments", moduleKey: "guides.payments", moduleLabel: "Extras Orders", permissionLabel: "Orders management" },
    ],
  },
  {
    group: "Operations",
    items: [
      { id: "operations-tasks", moduleKey: "operations.tasks", moduleLabel: "Tasks", permissionLabel: "Tasks" },
      { id: "operations-tasks-calendar", moduleKey: "operations.calendar", moduleLabel: "Tasks", permissionLabel: "Task calendar" },
      { id: "operations-checklists", moduleKey: "operations.checklists", moduleLabel: "Tasks", permissionLabel: "Checklists" },
      { id: "operations-work-orders", moduleKey: "operations.work_orders", moduleLabel: "Work Orders", permissionLabel: "Work orders" },
      { id: "operations-inspection", moduleKey: "operations.inspections", moduleLabel: "Inspection Board", permissionLabel: "Inspection board" },
      { id: "operations-inventory", moduleKey: "operations.inventory", moduleLabel: "Inventory", permissionLabel: "Inventory" },
      { id: "operations-standards", moduleKey: "operations.standards", moduleLabel: "Property Setup & Standards", permissionLabel: "Property setup and cleaning standards", accessLevels: ["none", "edit"] },
      { id: "operations-work-schedules", moduleKey: "operations.work_schedules", moduleLabel: "Work Schedules", permissionLabel: "Work schedules and availability" },
    ],
  },
  {
    group: "Reports",
    items: [
      { id: "reports-all", moduleKey: "reports", moduleLabel: "Reports", permissionLabel: "All reports and analytics", accessLevels: ["none", "view"] },
    ],
  },
  {
    group: "Admin",
    items: [
      { id: "admin-users", moduleKey: "admin.users", moduleLabel: "Users & Permissions", permissionLabel: "Invite users, assign non-admin roles, and manage zones", accessLevels: ["none", "edit"] },
      { id: "admin-roles", moduleKey: "admin.roles", moduleLabel: "Roles & Permissions", permissionLabel: "Manage roles and permissions", accessLevels: ["none", "edit"] },
      { id: "admin-settings", moduleKey: "admin.settings", moduleLabel: "Settings", permissionLabel: "Account/company settings", accessLevels: ["none", "edit"] },
      { id: "admin-billing", moduleKey: "admin.billing", moduleLabel: "Billing", permissionLabel: "Billing and subscription", accessLevels: ["none", "edit"] },
    ],
  },
];

export const PERMISSION_GROUPS = PERMISSION_MODULE_GROUPS.map((group) => ({
  group: group.group,
  items: group.items.map((item) => ({
    key: item.moduleKey,
    label: MODULE_LABELS[item.moduleKey] || item.moduleKey,
  })),
}));

export const ALL_MODULE_KEYS = [...new Set(PERMISSION_MODULE_GROUPS.flatMap((group) =>
  group.items.map((item) => item.moduleKey),
))];

export const ACCESS_LEVELS = [
  { value: "none", label: "None",  description: "No access. Menu hidden, routes blocked." },
  { value: "view", label: "View",  description: "Read-only. Can view and search." },
  { value: "edit", label: "Full Access",  description: "Create and edit within this section. User removal is owner-only." },
];

// Legacy export for any code still using ALL_PERMISSION_KEYS
export const ALL_PERMISSION_KEYS = ALL_MODULE_KEYS;
